import React, { useContext, useEffect, useState, useMemo } from 'react'
import moment from 'moment'
import { pdf } from '@react-pdf/renderer'
import { useSearchParams } from 'react-router-dom'
import { GlobalContext, UserContext } from '../../../App'

import API from '../../../services/API'
import CommandLogRow from './CommandLogRow'
import TacticalLogsPDF from './TacticalLogsPDF'
import AnDateField from '../../../components/DateTime/DateField'
import { Box, useTheme } from '@mui/material'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import ArrowLeft from '@mui/icons-material/ArrowLeft'
import ArrowRight from '@mui/icons-material/ArrowRight'
import Send from '@mui/icons-material/Send'
import PagePaper from '../../../components/mainFrame/PagePaper'

const TacticalLog = () => {
  const [loading, setLoading] = useState(true)
  const [commandLogs, setCommandLogs] = useState([])
  const [newLogText, setNewLogText] = useState('')
  const [newLogFlagged, setNewLogFlagged] = useState(false)
  const [newLogPinned, setNewLogPinned] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const startDate =
    searchParams.get('startDate') || moment().format('YYYY-MM-DD')
  const endDate = searchParams.get('endDate') || moment().format('YYYY-MM-DD')

  const handleDownloadPdf = async () => {
    const blob = await pdf(
      <TacticalLogsPDF
        logs={commandLogs}
        startDate={moment(startDate).format('Do MMMM YYYY')}
        endDate={moment(endDate).format('Do MMMM YYYY')}
        printed={`${user.firstName} ${user.lastName} on ${moment().format('Do MMM YYYY')}`}
      />,
    ).toBlob()

    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `Tactical_Logs_${moment(startDate).format('DDMMYYYY')}-${moment(endDate).format('DDMMYYYY')}.pdf`
    link.click()
    URL.revokeObjectURL(url)
  }

  const fetchCommandLogs = () => {
    if (
      !moment(startDate).isValid ||
      !moment(endDate).isValid ||
      moment(startDate).isAfter(moment()) ||
      moment(endDate).isAfter(moment())
    )
      return
    setLoading(true)
    API.get(
      `/commandcontrol/commandlogs?startDate=${moment(startDate).toISOString()}&endDate=${moment(endDate).toISOString()}`,
    )
      .then((res) => res.status === 200 && setCommandLogs(res.data))
      .catch((e) => console.log(e))
      .finally(() => setLoading(false))
  }

  useEffect(() => fetchCommandLogs(), [startDate, endDate])

  const { user } = useContext(UserContext)
  const { setToast } = useContext(GlobalContext)
  const theme = useTheme()

  const handleSubmitNewLog = (e) => {
    e.preventDefault()
    API.post('/commandcontrol/commandlog', {
      log: newLogText,
      pinned: newLogPinned,
      flagged: newLogFlagged,
    }).then((res) => {
      if (res.status === 200) {
        if (
          !moment().isSame(startDate, 'day') ||
          !moment().isSame(endDate, 'day')
        ) {
          setSearchParams((prev) => {
            const params = new URLSearchParams(prev)
            params.set('startDate', moment().format('YYYY-MM-DD'))
            params.set('endDate', moment().format('YYYY-MM-DD'))
            return params
          })
        } else {
          fetchCommandLogs()
        }
        setNewLogText('')
        setNewLogFlagged(false)
        setNewLogPinned(false)
      } else {
        setToast({
          message: 'Unable to submit log. Please try again later',
          severity: 'error',
        })
      }
    })
  }

  return (
    <PagePaper>
      <Stack gap={1}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          gap={1}
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="title2">Tactical Command Logs</Typography>
          <Stack direction="row" gap={2}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              fullWidth
              disabled={loading || commandLogs.length === 0}
              onClick={handleDownloadPdf}
            >
              Download PDF
            </Button>
          </Stack>
          <Stack direction="row" alignItems="center" gap={1}>
            <Stack direction={{ xs: 'column', lg: 'row' }} gap={2}>
              <Stack alignItems="center" direction="row">
                <ArrowLeft
                  sx={{ cursor: 'pointer' }}
                  color="anBlue"
                  onClick={() => {
                    setSearchParams((prev) => {
                      const params = new URLSearchParams(prev)
                      params.set(
                        'startDate',
                        moment(startDate)
                          .subtract(1, 'day')
                          .format('YYYY-MM-DD'),
                      )
                      return params
                    })
                  }}
                />
                <AnDateField
                  size="small"
                  fullWidth
                  date={startDate}
                  onChange={(newDate) => {
                    setSearchParams((prev) => {
                      const params = new URLSearchParams(prev)
                      params.set(
                        'startDate',
                        moment(newDate).format('YYYY-MM-DD'),
                      )
                      return params
                    })
                  }}
                />
                <ArrowRight
                  sx={{ cursor: 'pointer' }}
                  color="anBlue"
                  onClick={() => {
                    setSearchParams((prev) => {
                      const params = new URLSearchParams(prev)
                      params.set(
                        'startDate',
                        moment(startDate).add(1, 'day').format('YYYY-MM-DD'),
                      )
                      return params
                    })
                  }}
                />
              </Stack>
              {(startDate !== moment().format('YYYY-MM-DD') ||
                endDate !== moment().format('YYYY-MM-DD')) && (
                <Button
                  size="small"
                  onClick={() => {
                    setSearchParams((prev) => {
                      const params = new URLSearchParams(prev)
                      params.set('startDate', moment().format('YYYY-MM-DD'))
                      params.set('endDate', moment().format('YYYY-MM-DD'))
                      return params
                    })
                  }}
                  variant="outlined"
                  color="anBlue"
                >
                  Today
                </Button>
              )}
              <Stack alignItems="center" direction="row">
                <ArrowLeft
                  sx={{ cursor: 'pointer' }}
                  color="anBlue"
                  onClick={() => {
                    setSearchParams((prev) => {
                      const params = new URLSearchParams(prev)
                      params.set(
                        'endDate',
                        moment(endDate).subtract(1, 'day').format('YYYY-MM-DD'),
                      )
                      return params
                    })
                  }}
                />
                <AnDateField
                  size="small"
                  fullWidth
                  date={endDate}
                  onChange={(newDate) => {
                    setSearchParams((prev) => {
                      const params = new URLSearchParams(prev)
                      params.set(
                        'endDate',
                        moment(newDate).format('YYYY-MM-DD'),
                      )
                      return params
                    })
                  }}
                />
                <ArrowRight
                  sx={{ cursor: 'pointer' }}
                  color="anBlue"
                  onClick={() => {
                    setSearchParams((prev) => {
                      const params = new URLSearchParams(prev)
                      params.set(
                        'endDate',
                        moment(endDate).add(1, 'day').format('YYYY-MM-DD'),
                      )
                      return params
                    })
                  }}
                />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: 3,
            backgroundColor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
            boxShadow:
              '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 8px rgba(15, 23, 42, 0.06)',
          }}
        >
          <Stack component="form" onSubmit={handleSubmitNewLog} spacing={1.5}>
            <TextField
              placeholder="Write a new tactical log entry..."
              value={newLogText}
              onChange={(e) => setNewLogText(e.target.value)}
              multiline
              minRows={2}
              fullWidth
              autoComplete="off"
              variant="outlined"
            />

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box ml={1}>
                <Typography variant="smallGrey">
                  Add a clear, time-relevant update for the command log.
                </Typography>
              </Box>
              <Button
                color="primary"
                variant="contained"
                type="submit"
                disabled={!newLogText?.trim()}
                endIcon={<Send />}
                sx={{
                  minWidth: 120,
                }}
              >
                Submit
              </Button>
            </Stack>
          </Stack>
        </Paper>
        <Stack spacing={1.5}>
          {commandLogs
            .sort((a, b) => moment(b.createdAt) - moment(a.createdAt))
            .map((log) => (
              <CommandLogRow log={log} key={log._id} />
            ))}
        </Stack>
      </Stack>
    </PagePaper>
  )
}

export default TacticalLog
